import { useEffect, useMemo, useState } from "react";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { colors, spacing } from "../../theme";
import {
  DuplicateAuditListItem,
  DuplicateReviewLookups,
  LookupItem,
  SaveDuplicateAuditReviewRequest,
} from "../../types/duplicateAudit";
import { formatAuditDateTime } from "../../utils/duplicateAudit";

export type ReviewFieldErrors = Partial<Record<"outcomeId" | "rootCauseId" | "actionId", string>>;

interface Props {
  audit: DuplicateAuditListItem;
  lookups: DuplicateReviewLookups | null;
  saving: boolean;
  fieldErrors: ReviewFieldErrors;
  onSave: (request: SaveDuplicateAuditReviewRequest) => void;
}

// An existing review may reference a value that is no longer active; keep it selectable.
const withSaved = (items: LookupItem[], id: number | null, name: string | null): LookupItem[] =>
  id !== null && name && !items.some((i) => i.id === id) ? [...items, { id, name }] : items;

export default function ReviewForm({ audit, lookups, saving, fieldErrors, onSave }: Props) {
  const review = audit.review;
  const [outcomeId, setOutcomeId] = useState<number | "">("");
  const [rootCauseId, setRootCauseId] = useState<number | "">("");
  const [actionId, setActionId] = useState<number | "">("");
  const [note, setNote] = useState("");

  // Reload the form whenever the audit or its saved review changes (selection, save, reload after 409).
  useEffect(() => {
    setOutcomeId(review?.outcomeId ?? "");
    setRootCauseId(review?.rootCauseId ?? "");
    setActionId(review?.actionId ?? "");
    setNote(review?.note ?? "");
  }, [audit.auditId, review?.rowVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const outcomes = useMemo(
    () => withSaved(lookups?.outcomes ?? [], review?.outcomeId ?? null, review?.outcome ?? null),
    [lookups, review]
  );
  const rootCauses = useMemo(
    () => withSaved(lookups?.rootCauses ?? [], review?.rootCauseId ?? null, review?.rootCause ?? null),
    [lookups, review]
  );
  const actions = useMemo(
    () => withSaved(lookups?.actions ?? [], review?.actionId ?? null, review?.action ?? null),
    [lookups, review]
  );

  const handleSave = () =>
    onSave({
      outcomeId: outcomeId === "" ? null : outcomeId,
      rootCauseId: rootCauseId === "" ? null : rootCauseId,
      actionId: actionId === "" ? null : actionId,
      note: note.trim() === "" ? null : note,
      ...(review ? { rowVersion: review.rowVersion } : {}),
    });

  const select = (
    label: string,
    value: number | "",
    setValue: (v: number | "") => void,
    items: LookupItem[],
    error?: string
  ) => (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(e) => setValue(e.target.value === "" ? "" : Number(e.target.value))}
      error={!!error}
      helperText={error}
      disabled={!lookups}
    >
      <MenuItem value="">
        <em>Not set</em>
      </MenuItem>
      {items.map((i) => (
        <MenuItem key={i.id} value={i.id}>
          {i.name}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} mb={spacing.mui.md}>
        Reviewer Outcome
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: spacing.mui.md,
        }}
      >
        {select("Outcome", outcomeId, setOutcomeId, outcomes, fieldErrors.outcomeId)}
        {select("Root cause", rootCauseId, setRootCauseId, rootCauses, fieldErrors.rootCauseId)}
        {select("Action taken", actionId, setActionId, actions, fieldErrors.actionId)}
      </Box>
      <TextField
        label="Reviewer notes"
        multiline
        minRows={4}
        fullWidth
        value={note}
        onChange={(e) => setNote(e.target.value)}
        sx={{ mt: spacing.mui.md }}
      />
      <Box sx={{ mt: spacing.mui.md, display: "flex", alignItems: "center", gap: 2 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving || !lookups}>
          {saving ? "Saving..." : "Save review"}
        </Button>
        {review && (
          <Typography variant="caption" color={colors.text.secondary}>
            Last saved {formatAuditDateTime(review.modifiedAtUtc)} by {review.modifiedBy}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
